import React, { useEffect, useState } from "react";
import { Link, Navigate, redirect } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { signOut, deleteUser } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../context/AuthContext";

const Navbar = () => {
  const { cart = [] } = useCart();
  const { currentUser } = useAuth();
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [text, setText] = useState("In");

  useEffect(() => {
    const interval = setInterval(() => {
      setText((prev) => (prev === "In" ? "Up" : "In"));
    }, 9500);
    return () => clearInterval(interval);
  }, []);

  const username = currentUser?.displayName || currentUser?.email?.split("@")[0];

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to delete your account? This cannot be undone.")) {
      try {
        await deleteUser(auth.currentUser);
        alert("Your account has been deleted.");
      } catch (err) {
        console.error("Error deleting account:", err);
        alert("Please re-login before deleting your account.");
      }
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">

        {/* Logo */}
        <h1 className="text-2xl font-serif text-rose-600">
          <Link to="/">GlowAura</Link>
        </h1>

        {/* Center Welcome Text */}
        <div className="hidden md:block text-lg font-semibold text-rose-700">
          {currentUser && `Welcome ${username}`}
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex">
          <ul className="flex space-x-6 text-gray-700 font-medium items-center">
            <li><Link to="/" className="hover:text-rose-600">Home</Link></li>
            <li><Link to="/about" className="hover:text-rose-600">About</Link></li>
            <li><Link to="/products" className="hover:text-rose-600">Products</Link></li>
            <li><Link to="/blog" className="hover:text-rose-600">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-rose-600">Contact</Link></li>

            {/* ✅ If user is NOT logged in → Show Sign In */}
            {!currentUser ? (
              <li>
                <Link
                  to="/login"
                  className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm hover:bg-rose-700 transition"
                >
                  Sign {text}
                </Link>
              </li>
            ) : (
              /* ✅ My Account Dropdown */
              <li className="relative">
                <button
                  onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                  className="bg-gray-200 px-4 py-2 rounded-full hover:bg-gray-300 transition"
                >
                  My Account
                </button>

                {isAccountMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg">
                    <Link
                      to="/orders"
                      className="block px-4 py-2 hover:bg-gray-100"
                      onClick={() => setIsAccountMenuOpen(false)}
                    >
                      My Orders
                    </Link>

                    <Link
                      to="/change-password"
                      className="block px-4 py-2 hover:bg-gray-100"
                      onClick={() => setIsAccountMenuOpen(false)}
                    >
                      Change Password
                    </Link>

                    <button
                      onClick={() => {
                        handleDeleteAccount();
                        setIsAccountMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600"
                    >
                      Delete Account
                    </button>

                    <button
                      onClick={() => {
                        signOut(auth);
                        setIsAccountMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </li>
            )}

            {/* Cart */}
            <li className="relative">
              <Link to="/cart" className="text-rose-600 hover:text-rose-700">
                <ShoppingCart className="w-6 h-6" />
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-rose-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {totalItems}
                  </span>
                )}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-gray-700"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ✅ Mobile Menu (basic version, you can add dropdown here too) */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-md">
          <ul className="flex flex-col space-y-4 px-6 py-4 text-gray-700 font-medium">
            <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link></li>
            <li><Link to="/about" onClick={() => setIsMenuOpen(false)}>About</Link></li>
            <li><Link to="/products" onClick={() => setIsMenuOpen(false)}>Products</Link></li>
            <li><Link to="/blog" onClick={() => setIsMenuOpen(false)}>Blog</Link></li>
            <li><Link to="/contact" onClick={() => setIsMenuOpen(false)}>Contact</Link></li>

            <li className="relative">
              <Link to="/cart" onClick={() => setIsMenuOpen(false)}>
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Cart
                  {totalItems > 0 && (
                    <span className="bg-rose-600 text-white text-xs px-2 py-1 rounded-full">
                      {totalItems}
                    </span>
                  )}
                </div>
              </Link>
            </li>

            {!currentUser ? (
              <li>
                <Link
                  to="/login"
                  className="bg-rose-600 text-white px-4 py-2 rounded-full text-sm hover:bg-rose-700 transition"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign {text}
                </Link>
              </li>
            ) : (
              <li>
                <button
                  onClick={() => {
                    signOut(auth);
                    setIsMenuOpen(false);

                  }}
                  className="bg-gray-200 px-4 py-2 rounded-full hover:bg-gray-300 transition"
                >
                  Logout
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
